import { useEffect, useState } from 'react';
import { AlertCircle, ShieldOff } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { authErrorMessage } from '@/lib/auth';
import { useTwoFactorDisable } from '@/lib/two-factor';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TwoFactorDisableDialog({ open, onOpenChange }: Props) {
  const disable = useTwoFactorDisable();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');

  // `disable` mutation identity changes every render; must stay out of deps
  // to avoid reset→re-render→reset infinite loop. Only the open-edge matters.
  useEffect(() => {
    if (!open) {
      setCode('');
      setPassword('');
      disable.reset();
    }
  }, [open]);

  const handleSubmit = () => {
    disable.mutate(
      { code: code.replace(/\s+/g, ''), password },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <ShieldOff className="h-5 w-5" />
            ปิดใช้งาน 2FA
          </DialogTitle>
          <DialogDescription>
            การปิด 2FA จะทำให้บัญชีปลอดภัยน้อยลง — ใส่รหัสยืนยัน + รหัสผ่านเพื่อยืนยัน
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {disable.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{authErrorMessage(disable.error)}</AlertDescription>
            </Alert>
          )}
          <div>
            <Label htmlFor="disable-code" className="mb-1.5 block">
              รหัส 6 หลัก หรือ Backup code
            </Label>
            <Input
              id="disable-code"
              autoComplete="one-time-code"
              placeholder="123456 หรือ XXXXX-XXXXX"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="font-mono"
            />
          </div>
          <div>
            <Label htmlFor="disable-password" className="mb-1.5 block">
              รหัสผ่านบัญชี
            </Label>
            <PasswordInput
              id="disable-password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            ยกเลิก
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleSubmit}
            disabled={!code || !password || disable.isPending}
          >
            {disable.isPending ? 'กำลังปิด...' : 'ปิด 2FA'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
